import { Button, Flex, HStack, Icon, Spacer, VStack } from "@chakra-ui/react";
import { RadioCard, RadioCardGroup } from "components/FormElements/RadioCardGroup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { TargetType } from "features/RecordTarget/models";
import { User } from "features/User/models";
import { OnboardingJourneyNetworkRelation } from "models/v2/onboardingJourneyNetworkRelation";
import { titleize } from "helpers/string";
import CDS, { FloatingField } from "design-system";
import { MessageServiceChannel } from "models/integration/messageService";
import { FaHashtag } from "react-icons/fa";
import { MdOutlinePeopleAlt, MdPerson } from "react-icons/md";
import { useMemo } from "react";
import { IconType } from "react-icons";
import { Schema, buildRecordTargetSchema } from "./schema";

type TargetTypeField = {
  name: string;
  value: TargetType;
  icon: IconType;
};

export const SELECTABLE_TARGET_TYPE_FIELDS: TargetTypeField[] = [
  { name: "Role", value: "OnboardingJourneyNetworkRelation", icon: MdOutlinePeopleAlt },
  { name: "Specific User", value: "User", icon: MdPerson },
  { name: "Channel", value: "MessageServiceChannel", icon: FaHashtag },
];

const USER_FIELD = {
  name: "targetId",
  label: "Select User",
};

const RELATION_FIELD = {
  name: "targetId",
  label: "Select Role",
};

const MESSAGE_SERVICE_CHANNEL_FIELD = {
  label: "Channel",
  name: "targetId",
};

function RecipientTargetType({ name, icon }: { name: string; icon: any }) {
  return (
    <Flex direction="column" gap="2px" justify="center" alignItems="stretch" height="100%">
      <CDS.Text14 textAlign="center" fontWeight="600">
        {name}
      </CDS.Text14>
      <VStack alignItems="center" gap="0">
        <Icon as={icon} boxSize="6" color="fg.subtle" />
      </VStack>
    </Flex>
  );
}

interface RecordTargetFormProps {
  onSubmit: (data: Schema) => void;
  recordTargetTypes: TargetType[] | "All";
  defaultTargetType?: TargetType;
  label: string;
}

export default function RecordTargetForm({
  label,
  recordTargetTypes = "All",
  defaultTargetType = "OnboardingJourneyNetworkRelation",
  onSubmit,
}: RecordTargetFormProps) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(buildRecordTargetSchema(label, false)),
    defaultValues: { targetType: defaultTargetType },
  });
  const filteredFields = useMemo(
    () =>
      recordTargetTypes === "All"
        ? SELECTABLE_TARGET_TYPE_FIELDS
        : SELECTABLE_TARGET_TYPE_FIELDS.filter((item) => recordTargetTypes.includes(item.value)),
    [recordTargetTypes],
  );

  const targetType = watch("targetType");

  return (
    <>
      <RadioCardGroup<TargetType>
        direction="row"
        justify="stretch"
        value={targetType}
        spacing={3}
        onChange={(value) => setValue("targetType", value)}
        flexWrap="wrap"
        py={4}
      >
        {filteredFields.map((field) => (
          <RadioCard
            key={field.value}
            value={field.value}
            alignSelf="stretch"
            minW="120px"
            maxW="120px"
            minH="100px"
            maxH="100px"
          >
            <RecipientTargetType {...field} />
          </RadioCard>
        ))}
      </RadioCardGroup>
      {targetType === "User" && (
        <FloatingField name={USER_FIELD.name} errors={errors.targetId?.message}>
          <FloatingField.Label>{USER_FIELD.label}</FloatingField.Label>
          <FloatingField.SingleSelect<User>
            name={USER_FIELD.name}
            control={control}
            fetchPath={API_ROUTES.users.search}
            perPagePagination={300}
            extractValue={(user: User) => user.id}
            embedOption={(user: User) => ({
              value: user.id,
              label: user.fullName ?? "",
              rawValue: user,
            })}
          />
        </FloatingField>
      )}
      {targetType === "OnboardingJourneyNetworkRelation" && (
        <FloatingField name={RELATION_FIELD.name} errors={errors.targetId?.message}>
          <FloatingField.Label>{RELATION_FIELD.label}</FloatingField.Label>
          <FloatingField.SingleSelect<OnboardingJourneyNetworkRelation>
            name={RELATION_FIELD.name}
            control={control}
            fetchPath={API_ROUTES.onboardingJourneyNetworkRelations.base}
            extractValue={(v: OnboardingJourneyNetworkRelation) => v.id}
            embedOption={(v: OnboardingJourneyNetworkRelation) => ({
              value: v.id,
              label: `${titleize(v.name)}`,
              rawValue: v,
            })}
          />
        </FloatingField>
      )}
      {targetType === "MessageServiceChannel" && (
        <FloatingField name={MESSAGE_SERVICE_CHANNEL_FIELD.name} errors={errors.targetId?.message}>
          <FloatingField.Label>{MESSAGE_SERVICE_CHANNEL_FIELD.label}</FloatingField.Label>
          <FloatingField.SingleSelect<MessageServiceChannel>
            name={MESSAGE_SERVICE_CHANNEL_FIELD.name}
            control={control}
            fetchPath={API_ROUTES.messageServices.channels.base}
            extractValue={(v: MessageServiceChannel) => v.id}
            embedOption={(v) => ({
              value: v.id,
              label: `#${v.name}`,
              rawValue: v,
            })}
          />
        </FloatingField>
      )}
      <HStack>
        <Spacer />
        <Button onClick={handleSubmit(onSubmit)}>Add</Button>
      </HStack>
    </>
  );
}
